import { useQuery } from "react-query";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import Loader from "../components/loader";
import { ApiEndpoint } from "../components/utils";
import "./coupon.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { BrandInputWhite } from "../components/form/inputs";
import { BrandButton, BrandButtonWhite } from "../components/form/buttons";
import { Alert, Snackbar } from "@mui/material";
import { updateUserPaidStatus } from "../components/utils";
import NetworkError from "../components/networkError";
import {
  getUserInfo,
  setUserInfo,
  updatePaidExamStatus,
  userLoggedIn,
} from "../components/user";
import { CouponResponse, UserInfoRecord } from "../components/types";

interface PageResponse {
  success: boolean;
  coupon_amount?: string | number | undefined;
  message?: string;
}

interface ExamProps {
  examId: number;
  examFee: number;
  cCode?: string;
}

const Coupon = () => {
  const [searchingCoupon, setSearchingCoupon] = useState(false);
  const couponCode = useRef("");
  const location = useLocation();
  const [snackError, setSnackError] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const vertical = "bottom";
  const horizontal = "center";
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const { examId, examFee, cCode } = (location.state as ExamProps) || {};
  const [uData, setUserData] = useState<UserInfoRecord | null>(null);

  const applyCoupon = async () => {
    const userData = getUserInfo();
    await axios.post(ApiEndpoint + "wp-json/satapp-api/v1/applyCouponCode", {
      token: userData?.token,
      coupon_code: cCode,
    });
  };

  const { isLoading: couponLoading, error: couponError } = useQuery(
    "getCouponData",
    async () => {
      const token = uData?.token;
      let resp: CouponResponse | null = null;
      const url = cCode
        ? `${ApiEndpoint}wp-json/satapp-api/v1/checkCouponById`
        : `${ApiEndpoint}wp-json/satapp-api/v1/checkCoupon`;

      try {
        resp = await axios.post(url, {
          token,
          coupon_code: cCode ? cCode : couponCode.current,
        });
      } catch (e) {
        setHasError(true);
        return;
      }

      setSearchingCoupon(false);

      if (!resp) {
        setHasError(true);
        setPageLoading(false);
        return;
      }

      if (!resp.data.success) {
        setPageLoading(false);
        setSnackError(true);
      }

      if (resp.data.coupon_amount === examFee) {
        let paidResp = null;

        if (examId === -1) {
          setUserInfo({ couponCode: "", paid: true });
          applyCoupon();
          paidResp = await updateUserPaidStatus(uData?.token, uData?.userid);
        } else {
          updatePaidExamStatus(examId);
          navigate("/pay-confirm", {
            replace: true,
          });
        }

        if (paidResp) {
          navigate("/register-confirm", {
            replace: true,
          });
        } else {
          setPageLoading(false);
          setSnackError(true);
        }
      } else {
        parseCouponData(resp.data);
      }
    },
    { enabled: searchingCoupon }
  );

  const parseCouponData = (data: PageResponse) => {
    if (data && data.success) {
      let totalAmount = 0;
      if (data?.coupon_amount && typeof data?.coupon_amount === "number") {
        totalAmount = examFee - data.coupon_amount;
      }
      createPaymentIntent(totalAmount);
    } else {
      setPageLoading(false);
      setSnackError(true);
    }
  };

  const createPaymentIntent = async (amount: string | number | undefined) => {
    let result = null;
    try {
      result = await axios.post(
        ApiEndpoint + "wp-json/satapp-api/v1/createPayment",
        {
          amount: amount,
          metadata: {
            firstName: uData?.first_name,
            lastName: uData?.last_name,
            username: uData?.username,
          },
        }
      );
    } catch (e) {
      setSnackError(true);
      setPageLoading(false);
      return;
    }

    setPageLoading(false);
    if (result) {
      const token = uData?.token;
      const userid = uData?.userid;
      navigate("/register-pay", {
        replace: true,
        state: {
          paymentIntentId: result.data.data.paymentIntentId,
          clientSecret: result.data.data.clientSecret,
          token,
          userid,
          chargeAmount: amount,
          examId: examId,
        },
      });
    } else {
      setSnackError(true);
    }
  };

  useEffect(() => {
    if (!userLoggedIn()) {
      navigate("/");
    } else {
      setUserData(getUserInfo());
      if (cCode) {
        setPageLoading(true);
        setSearchingCoupon(true);
      }
    }
  }, []);

  // if (!token || !userid) return <LoadError />;
  if (couponError) setSnackError(true);
  if (couponLoading || pageLoading) return <Loader />;

  return (
    <div className="couponPage">
      <div className="headline">
        {examId ? (
          <>
            <h1>Exam Coupon</h1>
            <div
              className="description"
              dangerouslySetInnerHTML={{
                __html:
                  "If you have a coupon(s) for your exam, you can use it here.",
              }}
            />
          </>
        ) : (
          <>
            <h1>Thank You For Registering</h1>
            <div
              className="description"
              dangerouslySetInnerHTML={{
                __html: "You will now need to pay to take your exams.",
              }}
            />
          </>
        )}
      </div>
      <div className="coupon-container">
        <p>If you were provided a coupon code, enter it here.</p>
        <div className="coupon-input-row">
          <BrandInputWhite
            onChange={(evt) => {
              couponCode.current = evt.target.value;
            }}
            id="filled-basic"
            label="Your Coupon Code"
            variant="filled"
          />
          {couponLoading ? (
            <BrandButton>loading...</BrandButton>
          ) : (
            <BrandButton
              onClick={() => {
                setSearchingCoupon(true);
                setPageLoading(true);
              }}
            >
              Go
            </BrandButton>
          )}
        </div>
      </div>
      <div className="no-coupon-container">
        <p>Don't have a coupon code?</p>
        <BrandButtonWhite
          onClick={() => createPaymentIntent(examFee)}
          variant="outlined"
        >
          CONTINUE
        </BrandButtonWhite>
      </div>

      <Snackbar
        open={snackError}
        autoHideDuration={6000}
        onClose={() => setSnackError(false)}
        anchorOrigin={{ vertical, horizontal }}
      >
        <Alert severity="error">Sorry, No Coupon Found</Alert>
      </Snackbar>
      <NetworkError open={hasError} />
    </div>
  );
};

export default Coupon;
